import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import ContractTableRow from './ContractTableRow'
import { Contract, ContractStage } from '../../../../types'
import LoadingIcon from '../../../../../../../components/LoadingIcon'
import { CurrencyList } from '../../../types'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

type Props = {
    theme: string
    contractGuid: string
    contractRegNumber: string
    isActive: boolean
    currencyList: CurrencyList[]
    contractPrice: number
    contractRubPrice: number | null
    dataReloadToken: number
    contractVersion: number
    contractYear: number | null
    contractStage: string
}

/** форматирует числа “1 234,56” и обрабатывает пустые значения */
const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-'
    const parsedNum = typeof num === 'number' ? num : parseFloat(num)
    if (isNaN(parsedNum)) return '-'
    return parsedNum
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',')
}

const ContractTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    const [contracts, setContracts] = useState<Contract[]>([])
    const [contractsLoadedGuid, setContractsLoadedGuid] = useState<
        string | null
    >(null)
    const [contractsLoading, setContractsLoading] = useState(false)
    const [contractsError, setContractsError] = useState<string | null>(null)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [
        deleteContractPositionRowsPopup,
        setDeleteContractPositionRowsPopup,
    ] = useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deleteRows, setDeleteRows] = useState<Contract[]>([])

    // для отмены текущих фетчей
    const listAbortRef = useRef<AbortController | null>(null)
    const totalsAbortRef = useRef<AbortController | null>(null)

    const [updateFromEisPopup, setUpdateFromEisPopup] = useState<boolean>(false)
    const [isUpdatingFromEis, setUpdatingFromEis] = useState<boolean>(false)
    const [popupMessage, setPopupMessage] = useState('')
    const [forceUpdateUrl, setForceUpdateUrl] = useState<string | null>(null)

    const [updateErrorPopup, setUpdateErrorPopup] = useState<boolean>(false)
    const [updateErrorText, setUpdateErrorText] = useState<string>('')

    const fetchContractsRef = useRef<
        ((force?: boolean) => Promise<void>) | null
    >(null)

    // ====== loaders ======
    const fetchContracts = useCallback(
        async (force = false) => {
            if (!props.contractGuid) return
            if (!force && contractsLoadedGuid === props.contractGuid) return

            // важно: если форсим, сбросим метку
            if (force) setContractsLoadedGuid(null)

            if (listAbortRef.current) listAbortRef.current.abort()
            const controller = new AbortController()
            listAbortRef.current = controller

            try {
                setContractsLoading(true)
                setContractsError(null)

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/backend/v1/all_contract/contract_reg_number/${props.contractRegNumber}`,
                    {
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        signal: controller.signal,
                    }
                )
                if (!res.ok)
                    throw new Error('Failed to fetch contract positions')
                const data: Contract[] = await res.json()
                setContracts(data ?? [])
                setContractsLoadedGuid(props.contractGuid)

                // если стоял Select All — пересинхронизируем выбранные
                if (selectAll) {
                    setSelectedItems((data ?? []).map((i) => i.guid))
                } else {
                    // почистим выбранные, которых уже нет
                    setSelectedItems((prev) =>
                        prev.filter((id) =>
                            (data ?? []).some((d) => d.guid === id)
                        )
                    )
                }
            } catch (e: any) {
                if (e?.name !== 'AbortError') {
                    setContractsError(e?.message ?? 'Ошибка загрузки договоров')
                }
            } finally {
                setContractsLoading(false)
            }
        },
        [
            props.contractGuid,
            props.contractRegNumber,
            contractsLoadedGuid,
            selectAll,
        ]
    )

    useEffect(() => {
        fetchContractsRef.current = fetchContracts
    }, [fetchContracts])

    useEffect(() => {
        if (!props.isActive) return
        fetchContractsRef.current?.(true)
    }, [props.dataReloadToken, props.isActive])

    const addNewRow = useCallback(async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_new_draft_from?contract_reg_number=${props.contractRegNumber}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            if (!res.ok) throw new Error('Failed to create a new row')
            const created: Contract = await res.json()

            // добавляем новую запись локально без перезагрузки всего списка
            // setContracts((prev) => [created, ...prev])

            // НЕ сбрасываем contractsLoadedGuid — это и предотвращает автоперезагрузку
            // setContractsLoadedGuid(null)  // <-- удалить эту строку, не нужно

            navigate(
                `/hub/contract/${props.contractRegNumber}/contracts/draft/${created.guid}`,
                {
                    state: {
                        from: 'contract',
                        contractRegNumber: props.contractRegNumber,
                    },
                }
            )
        } catch (error) {
            console.error(error)
        }
    }, [props.contractRegNumber])

    const deleteRow = useCallback(async (guid: string): Promise<void> => {
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/contract/guid/${guid}`
        const response = await fetch(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(`Failed to delete row. Status: ${response.status}`)
        }
        const isNewDraftContract = props.contractVersion === 1

        if (isNewDraftContract) {
            navigate(
                `/hub/contracts?year=${props.contractYear}&stage=${props.contractStage}`,
                {
                    replace: true,
                }
            )
            return
        }
    }, [])

    const handleDeleteContractPositionRowsPopup = useCallback(() => {
        if (!deleteContractPositionRowsPopup) {
            const filteredData = contracts.filter((row) =>
                selectedItems.includes(row.guid)
            )
            setDeleteRows(filteredData)
        }
        setDeleteContractPositionRowsPopup((v) => !v)
    }, [deleteContractPositionRowsPopup, contracts, selectedItems])

    const handleDeleteClick = useCallback(async () => {
        if (selectedItems.length === 0) return
        setDeleting(true)

        const toDelete = [...selectedItems]
        const results = await Promise.allSettled(
            toDelete.map((id) => deleteRow(id))
        )

        // удалим из списка только те, что реально удалились
        const successIds = new Set(
            results
                .map((r, i) => ({ r, id: toDelete[i] }))
                .filter((x) => x.r.status === 'fulfilled')
                .map((x) => x.id)
        )

        setContracts((prev) => prev.filter((p) => !successIds.has(p.guid)))
        setSelectedItems((prev) => prev.filter((id) => !successIds.has(id)))
        setDeleteRows((prev) => prev.filter((r) => !successIds.has(r.guid)))

        if (results.some((r) => r.status === 'rejected')) {
            console.error('Некоторые строки удалить не удалось')
        }

        setDeleting(false)
        setDeleteContractPositionRowsPopup(false)
        setSelectAll(false)
    }, [selectedItems, deleteRow])

    const handleDeleteRow = useCallback((rowGuid: string) => {
        setDeleteRows((prev) => prev.filter((row) => row.guid !== rowGuid))
        setSelectedItems((prev) => prev.filter((id) => id !== rowGuid))
    }, [])

    const handleCheckboxChange = useCallback((perf_guid: string) => {
        setSelectedItems((prev) =>
            prev.includes(perf_guid)
                ? prev.filter((id) => id !== perf_guid)
                : [...prev, perf_guid]
        )
    }, [])

    const handleRightClickCheckboxChange = useCallback((perf_guid: string) => {
        setSelectedItems((prev) =>
            prev.includes(perf_guid) ? prev : [...prev, perf_guid]
        )
    }, [])

    const handleSelectAllChange = useCallback(() => {
        setSelectAll((prev) => {
            const next = !prev
            if (next) {
                const filterArr = contracts.filter(
                    (row) => row.is_draft == true
                )
                const allIds = filterArr.map((i) => i.guid)
                setSelectedItems(allIds)
            } else {
                setSelectedItems([])
            }
            return next
        })
    }, [contracts])

    useEffect(() => {
        fetchContracts()
        return () => {
            listAbortRef.current?.abort()
        }
    }, [fetchContracts])

    // ====== helpers for stage/status (поддержим и строковые значения, и enum) ======
    const isPlacedInEis = (p: Contract) =>
        p.contract_stage === ContractStage.PERF

    const isEditingStage = (p: Contract) =>
        p.contract_stage === 'Редактирование' // при желании добавь enum, если он есть

    const isDraftStatus = (p: Contract) => p.contract_status === 'Черновик' // при желании добавь enum, если он есть

    const isReadyStage = (p: Contract) =>
        p.contract_stage === 'Подготовка к размещению' // при желании добавь enum, если он есть

    const isReadyDraftStatus = (p: Contract) =>
        p.contract_status === 'Подготовка' // при желании добавь enum, если он есть

    const isDeletable = (p: Contract) =>
        (isEditingStage(p) && isDraftStatus(p)) ||
        (isReadyStage(p) && isReadyDraftStatus(p))

    const isUpdatable = (p: Contract) =>
        p.contract_stage === 'Отправка в ЕИС' &&
        p.contract_status === 'Отправлено в ЕИС' // при желании добавь enum, если он есть

    // ====== derived UI states ======
    const canAddContract = contracts.every(isPlacedInEis) // если пусто -> true

    const canUpdateContract = !contracts.every(isUpdatable) // если пусто -> true
    const notPlacedContractGuid: string | null =
        contracts.find((p) => !isPlacedInEis(p))?.guid ?? null

    const selectedContracts = contracts.filter((p) =>
        selectedItems.includes(p.guid)
    )

    // если хотя бы один guid не найден в contracts -> запрещаем удаление (на всякий случай)
    const canDeleteSelected =
        selectedItems.length > 0 &&
        selectedContracts.length === selectedItems.length &&
        selectedContracts.every(isDeletable)

    const handleUpdateFromEisPopup = () => {
        setUpdateFromEisPopup(!updateFromEisPopup)
        setPopupMessage('')
    }

    const updateContractFromEis = async (
        contract_guid: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/contract_from_eis?contract_guid=${contract_guid}`

        setUpdatingFromEis(true)

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            // Успешный апдейт — просто переходим на страницу редактирования
            if (response.ok) {
                const data = await response.json().catch(() => null)

                navigate(
                    `/hub/contract/${data?.contract_reg_number || props.contractRegNumber}?tab=Исполнение договора`
                )
                return
            }

            // --- обработка ошибок ---
            let errorData: any = null
            try {
                errorData = await response.json()
            } catch (e) {
                console.error('Failed to parse JSON from response:', e)
            }

            const detail: string =
                (errorData && (errorData.detail || errorData.message)) ||
                `Unexpected error. Status: ${response.status}`

            // 1) спец-кейс: уже обновлено
            if (detail.includes('уже обновлено')) {
                setPopupMessage(
                    `${detail}\nВыполнить обновление данных из ЕИС все равно?`
                )
                setForceUpdateUrl(
                    process.env.REACT_APP_API_URL +
                        `/api/backend/v1/contract_from_eis?contract_guid=${contract_guid}&force_update=true`
                )
                setUpdateFromEisPopup(true)
                return
            }

            // 2) любые другие ошибки — попап с textarea
            setUpdateErrorText(detail)
            setUpdateErrorPopup(true)
        } catch (error: any) {
            console.error('Error while updating contract from EIS:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
        }
    }

    const handleForceUpdate = async () => {
        if (!forceUpdateUrl) return

        setUpdatingFromEis(true)
        try {
            const response = await fetch(forceUpdateUrl, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                let errorData: any = null
                try {
                    errorData = await response.json()
                } catch (e) {
                    console.error('Failed to parse JSON from response:', e)
                }

                const detail: string =
                    (errorData && (errorData.detail || errorData.message)) ||
                    `Failed to force update contract. Status: ${response.status}`

                setUpdateErrorText(detail)
                setUpdateErrorPopup(true)
                return
            }

            // Успешный принудительный апдейт
            navigate(`/hub/contract/${props.contractRegNumber}?tab=Договор`)
        } catch (error: any) {
            console.error('Error while forcing update contract:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при принудительном обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
            setUpdateFromEisPopup(false)
        }
    }

    const handleUpdateErrorPopup = () => {
        const needReload = updateErrorText
            ?.toLowerCase()
            .includes('договор переведен')

        setUpdateErrorPopup(false)
        setUpdateErrorText('')

        if (needReload) {
            fetchContracts(true)
        }
    }

    return (
        <>
            <fieldset disabled={false}>
                <div
                    className={`flex flex-col w-full min-w-fit bg-[#e4e6e8] dark:bg-[#242225] rounded`}
                >
                    {/* toolbar */}
                    <div className="flex flex-row gap-1 p-1 h-11 rounded-t text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center">
                            <div
                                className="w-6 h-6 grid rounded-full place-items-center cursor-pointer hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleSelectAllChange}
                            >
                                <HoverTooltip tooltipText="Выбрать все">
                                    <input
                                        type="checkbox"
                                        className="p-2 cursor-pointer"
                                        checked={selectAll}
                                        readOnly
                                    />
                                </HoverTooltip>
                            </div>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Добавить */}
                            <button
                                onClick={addNewRow}
                                disabled={!canAddContract}
                                className={`w-6 h-6 grid rounded-full place-items-center ${
                                    canAddContract
                                        ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]'
                                        : ''
                                }`}
                            >
                                <HoverTooltip tooltipText="Создать изменение">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                canAddContract
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Обновить */}
                            <button
                                onClick={() => {
                                    if (notPlacedContractGuid) {
                                        updateContractFromEis(
                                            notPlacedContractGuid
                                        )
                                    }
                                }}
                                disabled={!notPlacedContractGuid}
                                className={`w-6 h-6 grid rounded-full place-items-center ${
                                    notPlacedContractGuid
                                        ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]'
                                        : ''
                                }`}
                            >
                                <HoverTooltip tooltipText="Обновить">
                                    {isUpdatingFromEis ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            id="plus"
                                        >
                                            <path
                                                fill={
                                                    notPlacedContractGuid
                                                        ? props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                        : '#8A8A8A'
                                                }
                                                d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z"
                                            ></path>
                                        </svg>
                                    )}
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Удалить */}
                            <button
                                onClick={handleDeleteContractPositionRowsPopup}
                                disabled={!canDeleteSelected}
                                className={`flex w-6 h-6 rounded-full items-center justify-center ${
                                    canDeleteSelected
                                        ? 'hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]'
                                        : ''
                                }`}
                            >
                                <HoverTooltip tooltipText="Удалить">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={`${canDeleteSelected ? (props.theme == 'dark' ? '#FFFFFF' : '#181818') : '#8A8A8A'} `}
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex flex-1 items-center justify-center text-center"></div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex flex-col w-28 items-center justify-end text-right"></div>
                        <div className="flex flex-col w-28 items-center justify-end text-right"></div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                    </div>

                    {/* name */}
                    <div className="flex flex-row gap-1 h-10 p-1 items-center justify-center text-[10px] text-[#616173] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex w-8 items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Стадия
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Статус
                        </div>
                        <div className="flex flex-1 items-center justify-center text-center">
                            Наименование
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Статус в ЕИС
                        </div>
                        <div className="flex w-20 items-center justify-center text-center">
                            Редакция
                        </div>
                        <div className="flex w-20 items-center justify-center text-center">
                            Дата размещения
                        </div>
                    </div>

                    {/* data */}
                    <div className="flex flex-col w-full gap-1 my-1 overflow-y custom-scrollbar">
                        {contractsLoading ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            contracts?.map((row, index) => (
                                <ContractTableRow
                                    key={row.guid}
                                    contractGuid={props.contractGuid}
                                    perf_number={index + 1}
                                    theme={props.theme}
                                    contract={row}
                                    isActive={props.isActive}
                                    currencyList={props.currencyList}
                                    selected={selectedItems.includes(row.guid)}
                                    onClick={(contract_guid) => {
                                        handleCheckboxChange(contract_guid)
                                    }}
                                    onRightClick={(contract_guid) => {
                                        handleRightClickCheckboxChange(
                                            contract_guid
                                        )
                                    }}
                                    onDoubleClick={(contract_guid) => {
                                        const contract_action =
                                            row.contract_stage ===
                                                ContractStage.EDIT ||
                                            row.contract_stage ===
                                                ContractStage.READY_FOR_PLACEMENT ||
                                            row.contract_stage ===
                                                ContractStage.CONTRACT_TO_EIS
                                                ? 'draft'
                                                : 'edit'
                                        navigate(
                                            `/hub/contract/${props.contractRegNumber}/contracts/${contract_action}/${contract_guid}`,
                                            {
                                                state: {
                                                    from: 'contract',
                                                    contractRegNumber:
                                                        props.contractRegNumber,
                                                },
                                            }
                                        )
                                    }}
                                />
                            ))
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex flex-row  h-11 text-[11px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                        <div className="flex w-3 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex flex-1 gap-3 items-center justify-start">
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                    ${!canAddContract ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                `}
                                disabled={!canAddContract}
                                onClick={addNewRow}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !canAddContract
                                                ? '#8A8A8A'
                                                : props.theme == 'dark'
                                                  ? '#FFFFFF'
                                                  : '#181818'
                                        }
                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                    ></path>
                                </svg>
                                Создать изменение
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${!notPlacedContractGuid ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                    `}
                                disabled={!notPlacedContractGuid}
                                onClick={() => {
                                    if (notPlacedContractGuid) {
                                        updateContractFromEis(
                                            notPlacedContractGuid
                                        )
                                    }
                                }}
                            >
                                {isUpdatingFromEis ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                notPlacedContractGuid
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z"
                                        ></path>
                                    </svg>
                                )}
                                Обновить
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${!canDeleteSelected ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] bg-[#F6F6F6] hover:bg-[#ed9cae] dark:bg-[#494849] dark:hover:bg-[#701f1f] dark:text-[#FFFFFF]'}
                                    `}
                                disabled={!canDeleteSelected}
                                onClick={handleDeleteContractPositionRowsPopup}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !canDeleteSelected
                                                ? '#8A8A8A'
                                                : props.theme == 'dark'
                                                  ? '#FFFFFF'
                                                  : '#181818'
                                        }
                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                    ></path>
                                </svg>
                                Удалить
                            </button>
                        </div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                    </div>
                </div>
            </fieldset>

            {deleteContractPositionRowsPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleDeleteContractPositionRowsPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[800px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="times-circle"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Удаление строки
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                Вы уверены, что хотите удалить данные строки?
                                Это действие является необратимым. Продолжить?
                            </label>
                            <div className="flex flex-row w-full space-x-5 pt-2">
                                <div className="flex flex-col w-full">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 bg-[#d2d5d8] dark:bg-[#333033] text-[#6A6A6A] dark:text-[#8A8A8A] text-[10px] px-1 py-2 rounded-t-lg">
                                        <div className="flex w-10 items-center justify-center text-center ">
                                            №
                                        </div>
                                        <div className="flex flex-1 items-center justify-center text-center ">
                                            Наименование
                                        </div>
                                        <div className="flex w-7 items-center justify-center "></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[200px] gap-1 py-1 px-1 grow bg-[#e4e6e8] dark:bg-[#1D1C1E] rounded-b-lg overflow-y-auto custom-scrollbar">
                                        <div className="flex flex-col w-full space-y-1">
                                            {deleteRows.map((row, index) => (
                                                <div
                                                    key={row.guid}
                                                    className="flex flex-row w-full rounded bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                >
                                                    <div className="flex flex-row w-full">
                                                        <div className="flex flex-row min-h-8 flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                            <div className="flex w-10 items-center justify-center ">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex flex-1 items-center justify-left p-1 ">
                                                                {`Сведения об исполнении по договору № ${row.registration_number ? row.registration_number : '-'}`}
                                                            </div>
                                                            <div className="flex w-8 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                <HoverTooltip tooltipText="Удалить строку">
                                                                    <button
                                                                        onClick={() =>
                                                                            handleDeleteRow(
                                                                                row.guid
                                                                            )
                                                                        }
                                                                        className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]"
                                                                    >
                                                                        <svg
                                                                            className="w-3 h-3"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 24 24"
                                                                            id="trash-alt"
                                                                        >
                                                                            <path
                                                                                fill={
                                                                                    props.theme ==
                                                                                    'dark'
                                                                                        ? '#FFFFFF'
                                                                                        : '#181818'
                                                                                }
                                                                                d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                            ></path>
                                                                        </svg>
                                                                    </button>
                                                                </HoverTooltip>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-2">
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] ml-2 items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]`}
                                onClick={handleDeleteClick}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Удалить'
                                )}
                            </button>
                            {isDeleting ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteContractPositionRowsPopup
                                    }
                                >
                                    Отмена
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {updateFromEisPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleUpdateFromEisPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[450px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row gap-2 items-center justify-left">
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Обновление данных
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <div className="flex flex-row gap-2 items-center justify-left">
                                <div className="flex flex-row w-5 h-5 items-center justify-center text-[#181818] dark:text-[#FFFFFF]">
                                    <svg
                                        id="info-circle"
                                        className="w-5 h-5"
                                        data-name="Layer 1"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            fill="#FFBF00"
                                            d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                        ></path>
                                    </svg>
                                </div>
                                <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    {popupMessage}
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            {isUpdatingFromEis ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleUpdateFromEisPopup}
                                >
                                    Отмена
                                </button>
                            )}
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-full shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] ml-2 items-center justify-center ${!isUpdatingFromEis ? 'bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]' : 'bg-[#494849]'} `}
                                onClick={() => handleForceUpdate()} // Вызвать handleForceUpdate
                                disabled={isUpdatingFromEis ? true : false}
                            >
                                {isUpdatingFromEis ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Обновить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {updateErrorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleUpdateErrorPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[600px] h-fit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-start gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Ошибки при обновлении данных из ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1 gap-2">
                            <div className="flex flex-col mt-2 space-y-1">
                                <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                    Внимание! При обновлении исполнения из ЕИС
                                    возникли ошибки:
                                </label>
                            </div>
                            <div className="flex flex-col mt-2 space-y-1">
                                <textarea
                                    className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                    value={updateErrorText}
                                    rows={21}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleUpdateErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ContractTable
